import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { inputStyle, labelStyle, BRAND_PRIMARY, BRAND_ACCENT } from "../utils/constants";
import { formatBalance } from "../utils/formatting";

export interface MethodParams {
    tradeSize: [number, number];
    slippage: number;
    concurrency: number;
    timeInterval: [number, number];
    startIndex: number;
    numberOfWallets?: number;
    walletRange?: [number, number];
    walletIndex?: number;
    extractRatio?: number;
    reserveTargetInSOL?: number;
    injectionAmount?: number;
    buyToSellRatio?: number;
    priceThreshold?: number;
    mcThreshold?: number;
    thresholdType?: 'price' | 'marketcap';
    // Separate buy/sell trade sizes
    useSeparateTradeSizes?: boolean;
    buyTradeSize?: [number, number];
    sellTradeSize?: [number, number];
    autoRatioEnabled?: boolean;
    recoveryThreshold?: number;
    enableTrailing?: boolean;
    trailingPct?: number;
    targetAmount?: number;
    triggerType?: 'schedule' | 'price' | 'marketcap';
    maxVolume?: number;
    maxSellTokens?: number;
}

export interface BalanceSnapshot {
    sol: number;
    spl: number;
    wsol: number;
    quote?: number;
    updatedAt: number;
}

interface BotMethodFormProps {
    instanceId: string;
    method: string;
    params: Partial<MethodParams>;
    isRunning: boolean;
    onToggle: (isRunning: boolean, params: MethodParams, isUpdate?: boolean) => void;
    reservedWallets?: { index: number; address: string }[];
    canUseAltAccounts?: boolean;
    canUseSeparateTradeSizes?: boolean;
    canUseTrailingStop?: boolean;
    baseSymbol?: string;
    quoteSymbol?: string;
    poolType?: string;
    minInterval?: number;
}

const BotMethodForm: React.FC<BotMethodFormProps> = ({
    instanceId,
    method,
    params,
    isRunning,
    onToggle,
    reservedWallets,
    canUseAltAccounts = true,
    canUseSeparateTradeSizes = false,
    canUseTrailingStop = false,
    baseSymbol = 'TOKEN',
    quoteSymbol = 'SOL',
    poolType,
    minInterval = 30,
}) => {
    const [useAltWallet, setUseAltWallet] = useState(
        Number.isInteger(params?.walletIndex),
    );
    const [copyLabel, setCopyLabel] = useState("Copy");
    const [concurrencyDropdownOpen, setConcurrencyDropdownOpen] = useState(false);
    const [walletDropdownOpen, setWalletDropdownOpen] = useState(false);
    const concurrencyDropdownRef = useRef<HTMLDivElement>(null);
    const walletDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (concurrencyDropdownRef.current && !concurrencyDropdownRef.current.contains(e.target as Node)) {
                setConcurrencyDropdownOpen(false);
            }
            if (walletDropdownRef.current && !walletDropdownRef.current.contains(e.target as Node)) {
                setWalletDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const [formStrings, setFormStrings] = useState({
        tradeSizeMin: String(params?.tradeSize?.[0] ?? 0.1),
        tradeSizeMax: String(params?.tradeSize?.[1] ?? 1),
        slippage: String(params?.slippage ?? 1),
        concurrency: String(params?.concurrency ?? 1),
        timeIntervalMin: String(Math.max(params?.timeInterval?.[0] ?? 15, minInterval)),
        timeIntervalMax: String(Math.max(params?.timeInterval?.[1] ?? 60, minInterval)),
        numberOfWallets: String(params?.numberOfWallets ?? params?.walletRange?.[1] ?? 100),
        extractRatio: String(params?.extractRatio ?? 10),
        walletIndex: String(params?.walletIndex ?? ""),
        reserveTargetInSOL: String(params?.reserveTargetInSOL ?? 500),
        injectionAmount: String(params?.injectionAmount ?? 10),
        buyToSellRatio: String(Math.round((params?.buyToSellRatio ?? 0.5) * 100)),
        priceThreshold: String(params?.priceThreshold ?? 0),
        mcThreshold: String(params?.mcThreshold ?? 0),
        maxVolume: String(params?.maxVolume ?? ''),
        recoveryThreshold: String(params?.recoveryThreshold ?? 1),
        trailingPct: String(params?.trailingPct ?? 10),
        targetAmount: String(params?.targetAmount ?? ''),
        maxSellTokens: String(params?.maxSellTokens ?? ''),
    });
    const [thresholdType, setThresholdType] = useState<'price' | 'marketcap'>(
        params?.thresholdType ?? 'price'
    );
    const [enableTrailing, setEnableTrailing] = useState(params?.enableTrailing || false);
    const [triggerType, setTriggerType] = useState<'schedule' | 'price' | 'marketcap'>(params?.triggerType ?? 'schedule');
    // Separate buy/sell trade sizes state
    const [useSeparateTradeSizes, setUseSeparateTradeSizes] = useState(params?.useSeparateTradeSizes || false);
    const [buyTradeSizeMin, setBuyTradeSizeMin] = useState(String(params?.buyTradeSize?.[0] ?? 0.1));
    const [buyTradeSizeMax, setBuyTradeSizeMax] = useState(String(params?.buyTradeSize?.[1] ?? 1));
    const [sellTradeSizeMin, setSellTradeSizeMin] = useState(String(params?.sellTradeSize?.[0] ?? 0.1));
    const [sellTradeSizeMax, setSellTradeSizeMax] = useState(String(params?.sellTradeSize?.[1] ?? 1));
    const [autoRatioEnabled, setAutoRatioEnabled] = useState(params?.autoRatioEnabled ?? true);
    const [selectedWalletBalance, setSelectedWalletBalance] = useState<BalanceSnapshot | null>(null);
    const [selectedWalletLoading, setSelectedWalletLoading] = useState(false);
    const [selectedWalletError, setSelectedWalletError] = useState<string | null>(null);
    const [poolInfo, setPoolInfo] = useState<{ quoteReserve?: number; tokenPriceUSD?: number; solUsdPrice?: number } | null>(null);
    const [poolInfoLoading, setPoolInfoLoading] = useState(false);

    const fetchPoolInfo = useCallback(async () => {
        setPoolInfoLoading(true);
        try {
            const res = await axios.get(`/api/bot/${instanceId}/pool-info`);
            setPoolInfo(res.data);
        } catch (err) {
            console.error("Failed to fetch pool info:", err);
        } finally {
            setPoolInfoLoading(false);
        }
    }, [instanceId]);

    useEffect(() => {
        setPoolInfo(null);
    }, [instanceId]);

    // Reset ALL form state when switching bots
    useEffect(() => {
        setFormStrings({
            tradeSizeMin: String(params?.tradeSize?.[0] ?? 0.1),
            tradeSizeMax: String(params?.tradeSize?.[1] ?? 1),
            slippage: String(params?.slippage ?? 1),
            concurrency: String(params?.concurrency ?? 1),
            timeIntervalMin: String(Math.max(params?.timeInterval?.[0] ?? 15, minInterval)),
            timeIntervalMax: String(Math.max(params?.timeInterval?.[1] ?? 60, minInterval)),
            numberOfWallets: String(params?.numberOfWallets ?? params?.walletRange?.[1] ?? 100),
            extractRatio: String(params?.extractRatio ?? 10),
            walletIndex: String(params?.walletIndex ?? ""),
            reserveTargetInSOL: String(params?.reserveTargetInSOL ?? 500),
            injectionAmount: String(params?.injectionAmount ?? 10),
            buyToSellRatio: String(Math.round((params?.buyToSellRatio ?? 0.5) * 100)),
            priceThreshold: String(params?.priceThreshold ?? 0),
            mcThreshold: String(params?.mcThreshold ?? 0),
            maxVolume: String(params?.maxVolume ?? ''),
            recoveryThreshold: String(params?.recoveryThreshold ?? 1),
            trailingPct: String(params?.trailingPct ?? 10),
            targetAmount: String(params?.targetAmount ?? ''),
            maxSellTokens: String(params?.maxSellTokens ?? ''),
        });
        setThresholdType(params?.thresholdType ?? 'price');
        setEnableTrailing(params?.enableTrailing || false);
        setTriggerType(params?.triggerType ?? 'schedule');
        setUseSeparateTradeSizes(params?.useSeparateTradeSizes || false);
        setBuyTradeSizeMin(String(params?.buyTradeSize?.[0] ?? 0.1));
        setBuyTradeSizeMax(String(params?.buyTradeSize?.[1] ?? 1));
        setSellTradeSizeMin(String(params?.sellTradeSize?.[0] ?? 0.1));
        setSellTradeSizeMax(String(params?.sellTradeSize?.[1] ?? 1));
        setAutoRatioEnabled(params?.autoRatioEnabled ?? true);
        setUseAltWallet(Number.isInteger(params?.walletIndex));
        setSelectedWalletBalance(null);
        setSelectedWalletError(null);
    }, [instanceId]);

    useEffect(() => {
        const hasWalletIndex = Number.isInteger(params?.walletIndex);
        setUseAltWallet(hasWalletIndex);
        setFormStrings((prev) => ({
            ...prev,
            walletIndex: hasWalletIndex ? String(params?.walletIndex) : "",
        }));
    }, [params?.walletIndex]);

    useEffect(() => {
        const walletIndex = parseInt(formStrings.walletIndex, 10);
        if (!useAltWallet || !Number.isInteger(walletIndex)) {
            setSelectedWalletBalance(null);
            setSelectedWalletError(null);
            setSelectedWalletLoading(false);
            return;
        }
        let isMounted = true;
        const fetchBalance = async () => {
            setSelectedWalletLoading(true);
            setSelectedWalletError(null);
            try {
                const res = await axios.get(`/api/bot/${instanceId}/wallet/${walletIndex}/balance`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });
                if (isMounted) {
                    setSelectedWalletBalance(res.data.balances);
                }
            } catch (err: unknown) {
                if (isMounted) {
                    setSelectedWalletBalance({ sol: 0, spl: 0, wsol: 0, updatedAt: Date.now() });
                    setSelectedWalletError("Wallet not funded yet.");
                }
            } finally {
                if (isMounted) {
                    setSelectedWalletLoading(false);
                }
            }
        };
        fetchBalance();
        return () => {
            isMounted = false;
        };
    }, [formStrings.walletIndex, instanceId, useAltWallet]);

    // Auto-calculate buy/sell ratio when separate trade sizes are enabled
    useEffect(() => {
        if (!useSeparateTradeSizes || !autoRatioEnabled) return;

        const avgBuy = (parseFloat(buyTradeSizeMin) + parseFloat(buyTradeSizeMax)) / 2;
        const avgSell = (parseFloat(sellTradeSizeMin) + parseFloat(sellTradeSizeMax)) / 2;
        if (avgBuy + avgSell === 0) return;

        const ratioPercent = Math.round((avgSell / (avgBuy + avgSell)) * 100);
        setFormStrings(prev => ({
            ...prev,
            buyToSellRatio: String(Math.min(100, Math.max(0, ratioPercent)))
        }));
    }, [useSeparateTradeSizes, autoRatioEnabled, buyTradeSizeMin, buyTradeSizeMax, sellTradeSizeMin, sellTradeSizeMax]);

    // Handler for manual slider change - clears auto mode
    const handleBuySellRatioChange = (value: string) => {
        if (autoRatioEnabled && useSeparateTradeSizes) setAutoRatioEnabled(false);
        setFormStrings({ ...formStrings, buyToSellRatio: value });
    };

    const getFormParams = (): MethodParams => ({
        tradeSize: [
            parseFloat(formStrings.tradeSizeMin) || 0.1,
            parseFloat(formStrings.tradeSizeMax) || 1,
        ],
        slippage: parseFloat(formStrings.slippage) || 1,
        concurrency: parseInt(formStrings.concurrency) || 1,
        timeInterval: [
            Math.max(parseFloat(formStrings.timeIntervalMin) || 15, minInterval),
            Math.max(parseFloat(formStrings.timeIntervalMax) || 60, minInterval),
        ],
        startIndex: 1,
        numberOfWallets: parseInt(formStrings.numberOfWallets) || 100,
        extractRatio: parseInt(formStrings.extractRatio) || 10,
        ...(useAltWallet && formStrings.walletIndex
            ? { walletIndex: parseInt(formStrings.walletIndex) }
            : {}),
        reserveTargetInSOL: parseFloat(formStrings.reserveTargetInSOL) || 500,
        injectionAmount: parseFloat(formStrings.injectionAmount) || 10,
        buyToSellRatio: (parseInt(formStrings.buyToSellRatio, 10) || 50) / 100,
        priceThreshold: parseFloat(formStrings.priceThreshold) || 0,
        mcThreshold: parseFloat(formStrings.mcThreshold) || 0,
        thresholdType: thresholdType,
        maxVolume: formStrings.maxVolume ? parseFloat(formStrings.maxVolume) : undefined,
        recoveryThreshold: parseFloat(formStrings.recoveryThreshold) || 1,
        enableTrailing: enableTrailing,
        trailingPct: parseFloat(formStrings.trailingPct) || 10,
        triggerType: triggerType,
        targetAmount: formStrings.targetAmount ? parseFloat(formStrings.targetAmount) : undefined,
        maxSellTokens: formStrings.maxSellTokens ? parseFloat(formStrings.maxSellTokens) : undefined,
        // Separate buy/sell trade sizes
        ...(useSeparateTradeSizes ? {
            useSeparateTradeSizes: true,
            buyTradeSize: [
                parseFloat(buyTradeSizeMin) || 0.1,
                parseFloat(buyTradeSizeMax) || 1,
            ],
            sellTradeSize: [
                parseFloat(sellTradeSizeMin) || 0.1,
                parseFloat(sellTradeSizeMax) || 1,
            ],
            autoRatioEnabled: autoRatioEnabled,
        } : {}),
    });

    const handleToggle = () => {
        onToggle(!isRunning, getFormParams());
    };

    const handleUpdateParams = () => {
        onToggle(true, getFormParams(), true);
    };

    const handleToggleAltWallet = (checked: boolean) => {
        setUseAltWallet(checked);
        if (!checked) {
            setFormStrings({ ...formStrings, walletIndex: "" });
        }
    };

    const handleCopyWallet = async () => {
        const selectedIndex = parseInt(formStrings.walletIndex);
        const selectedAddress = reservedWallets?.find(
            (wallet) => wallet.index === selectedIndex,
        )?.address;
        if (!selectedAddress) return;
        try {
            await navigator.clipboard.writeText(selectedAddress);
            setCopyLabel("Copied");
            setTimeout(() => setCopyLabel("Copy"), 1200);
        } catch (err) {
            console.error("Failed to copy wallet address:", err);
        }
    };

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div style={{ opacity: method === "volume" && useSeparateTradeSizes ? 0.5 : 1 }}>
                    <label style={labelStyle}>
                        Trade Size (Min/Max)
                        {method === "volume" && useSeparateTradeSizes && (
                            <span style={{
                                marginLeft: '8px',
                                fontSize: '10px',
                                color: '#6b7280',
                                fontWeight: 'normal',
                            }}>
                                (Using separate sizes below)
                            </span>
                        )}
                    </label>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <input
                            type="number"
                            step="0.001"
                            value={formStrings.tradeSizeMin}
                            onChange={(e) => setFormStrings({ ...formStrings, tradeSizeMin: e.target.value })}
                            className="input-dark"
                            style={{
                                ...inputStyle,
                                cursor: method === "volume" && useSeparateTradeSizes ? 'not-allowed' : undefined,
                            }}
                            disabled={method === "volume" && useSeparateTradeSizes}
                        />
                        <input
                            type="number"
                            step="0.001"
                            value={formStrings.tradeSizeMax}
                            onChange={(e) => setFormStrings({ ...formStrings, tradeSizeMax: e.target.value })}
                            className="input-dark"
                            style={{
                                ...inputStyle,
                                cursor: method === "volume" && useSeparateTradeSizes ? 'not-allowed' : undefined,
                            }}
                            disabled={method === "volume" && useSeparateTradeSizes}
                        />
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>Slippage (%)</label>
                    <input
                        type="number"
                        value={formStrings.slippage}
                        onChange={(e) => setFormStrings({ ...formStrings, slippage: e.target.value })}
                        className="input-dark"
                        style={inputStyle}
                    />
                </div>

                <div ref={concurrencyDropdownRef} style={{ position: "relative" }}>
                    <label style={labelStyle}>Concurrency</label>
                    <button
                        type="button"
                        onClick={() => setConcurrencyDropdownOpen(!concurrencyDropdownOpen)}
                        className="input-dark"
                        style={{
                            ...inputStyle,
                            cursor: "pointer",
                            textAlign: "left",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>{formStrings.concurrency}</span>
                        <svg
                            style={{
                                width: "14px",
                                height: "14px",
                                flexShrink: 0,
                                transform: concurrencyDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.15s",
                            }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {concurrencyDropdownOpen && (
                        <div style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            right: 0,
                            background: "#1a1a2e",
                            border: "1px solid rgba(255,255,255,0.12)",
                            borderRadius: "8px",
                            zIndex: 50,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                            maxHeight: "240px",
                            overflowY: "auto",
                        }}>
                            {(poolType?.startsWith('meteora') ? ["1", "2", "3", "4"] : ["1", "2", "3", "4", "5"]).map((val) => (
                                <button
                                    key={val}
                                    type="button"
                                    className="dropdown-item"
                                    onClick={() => {
                                        setFormStrings({ ...formStrings, concurrency: val });
                                        setConcurrencyDropdownOpen(false);
                                    }}
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "10px 12px",
                                        background: val === formStrings.concurrency ? "rgba(153,69,255,0.12)" : "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        textAlign: "left",
                                        color: "white",
                                        fontSize: "14px",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (val !== formStrings.concurrency) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (val !== formStrings.concurrency) e.currentTarget.style.background = "transparent";
                                    }}
                                >
                                    {val}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <label style={labelStyle}>Time Interval (s) <span style={{ fontWeight: 400, opacity: 0.5 }}>min {minInterval}s</span></label>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <input
                            type="number"
                            step="0.1"
                            min={minInterval}
                            value={formStrings.timeIntervalMin}
                            onChange={(e) => setFormStrings({ ...formStrings, timeIntervalMin: e.target.value })}
                            onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val < minInterval) {
                                    setFormStrings((prev) => ({ ...prev, timeIntervalMin: String(minInterval) }));
                                }
                            }}
                            className="input-dark"
                            style={inputStyle}
                        />
                        <input
                            type="number"
                            step="0.1"
                            min={minInterval}
                            value={formStrings.timeIntervalMax}
                            onChange={(e) => setFormStrings({ ...formStrings, timeIntervalMax: e.target.value })}
                            onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val < minInterval) {
                                    setFormStrings((prev) => ({ ...prev, timeIntervalMax: String(minInterval) }));
                                }
                            }}
                            className="input-dark"
                            style={inputStyle}
                        />
                    </div>
                </div>

                {method !== "volume" && (
                    <>
                        <div>
                            <label style={labelStyle}># of wallets</label>
                            <input
                                type="number"
                                value={formStrings.numberOfWallets}
                                onChange={(e) => setFormStrings({ ...formStrings, numberOfWallets: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                            />
                        </div>

                        {method === "extract" && (
                            <div>
                                <label style={labelStyle} title="What percentage of each reserve increase to sell back. 10% = sell 10¢ of every $1 of buys.">% to Extract</label>
                                <input
                                    type="number"
                                    value={formStrings.extractRatio}
                                    onChange={(e) => setFormStrings({ ...formStrings, extractRatio: e.target.value })}
                                    className="input-dark"
                                    style={inputStyle}
                                />
                            </div>
                        )}

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Use alternate wallet</label>
                            {canUseAltAccounts ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <input
                                        type="checkbox"
                                        checked={useAltWallet}
                                        onChange={(e) => handleToggleAltWallet(e.target.checked)}
                                    />
                                    <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                                        Use a different wallet for {method}
                                    </span>
                                </div>
                            ) : (
                                <div style={{ fontSize: "12px", color: "#6b7280", opacity: 0.7 }}>
                                    Alternate wallets require 10M+ tokens or Pro
                                </div>
                            )}
                        </div>

                        {useAltWallet && canUseAltAccounts && (
                            <div style={{ gridColumn: "span 2" }}>
                                <label style={labelStyle}>Alternate wallet</label>
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <div ref={walletDropdownRef} style={{ position: "relative", flex: 1 }}>
                                        <button
                                            type="button"
                                            onClick={() => setWalletDropdownOpen(!walletDropdownOpen)}
                                            className="input-dark"
                                            style={{
                                                ...inputStyle,
                                                cursor: "pointer",
                                                textAlign: "left",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                            }}
                                        >
                                            <span style={{ fontSize: "12px" }}>
                                                {formStrings.walletIndex
                                                    ? (() => { const addr = (reservedWallets || []).find(w => String(w.index) === formStrings.walletIndex)?.address; return addr ? `${addr.slice(0, 4)}...${addr.slice(-4)}` : "Select wallet"; })()
                                                    : "Select wallet"}
                                            </span>
                                            <svg
                                                style={{
                                                    width: "14px",
                                                    height: "14px",
                                                    flexShrink: 0,
                                                    marginLeft: "8px",
                                                    transform: walletDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                    transition: "transform 0.15s",
                                                }}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        {walletDropdownOpen && (
                                            <div style={{
                                                position: "absolute",
                                                top: "calc(100% + 4px)",
                                                left: 0,
                                                right: 0,
                                                background: "#1a1a2e",
                                                border: "1px solid rgba(255,255,255,0.12)",
                                                borderRadius: "8px",
                                                zIndex: 50,
                                                boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                                                maxHeight: "240px",
                                                overflowY: "auto",
                                            }}>
                                                {(reservedWallets || []).map((wallet) => (
                                                    <button
                                                        key={wallet.index}
                                                        type="button"
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            setFormStrings({ ...formStrings, walletIndex: String(wallet.index) });
                                                            setWalletDropdownOpen(false);
                                                        }}
                                                        style={{
                                                            width: "100%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            padding: "10px 12px",
                                                            background: String(wallet.index) === formStrings.walletIndex ? "rgba(153,69,255,0.12)" : "transparent",
                                                            border: "none",
                                                            cursor: "pointer",
                                                            textAlign: "left",
                                                            color: "white",
                                                            fontSize: "12px",
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (String(wallet.index) !== formStrings.walletIndex) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (String(wallet.index) !== formStrings.walletIndex) e.currentTarget.style.background = "transparent";
                                                        }}
                                                    >
                                                        {wallet.address.slice(0, 4)}...{wallet.address.slice(-4)}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleCopyWallet}
                                        disabled={!formStrings.walletIndex}
                                        style={{
                                            padding: "10px 14px",
                                            borderRadius: "10px",
                                            border: "1px solid rgba(255,255,255,0.2)",
                                            background: "rgba(255,255,255,0.06)",
                                            color: "white",
                                            cursor: formStrings.walletIndex ? "pointer" : "not-allowed",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {copyLabel}
                                    </button>
                                </div>
                                <div style={{ marginTop: "10px", fontSize: "12px", color: "#9ca3af" }}>
                                    {selectedWalletLoading && <span>Loading wallet balance...</span>}
                                    {!selectedWalletLoading && selectedWalletBalance && (
                                        <span>
                                            Wallet balance: {formatBalance(selectedWalletBalance.sol, 4)} SOL
                                            {quoteSymbol !== "SOL" && selectedWalletBalance.quote !== undefined ? (
                                                <> / {formatBalance(selectedWalletBalance.quote, 4)} {quoteSymbol}</>
                                            ) : null}
                                            {" / "}
                                            {formatBalance(selectedWalletBalance.spl, 4)} {baseSymbol}
                                        </span>
                                    )}
                                    {!selectedWalletLoading && selectedWalletError && (
                                        <span style={{ color: "#f87171" }}> {selectedWalletError}</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {method === "volume" && (
                    <>
                        <div>
                            <label style={labelStyle}>
                                Buy/Sell Balance
                                {useSeparateTradeSizes && autoRatioEnabled && (
                                    <span style={{
                                        marginLeft: '8px',
                                        padding: '2px 6px',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        borderRadius: '4px',
                                        background: 'rgba(255, 123, 0, 0.2)',
                                        color: BRAND_PRIMARY,
                                    }}>
                                        Auto
                                    </span>
                                )}
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={formStrings.buyToSellRatio}
                                onChange={(e) => handleBuySellRatioChange(e.target.value)}
                                className="slider-dark"
                                style={{ width: '100%' }}
                            />
                            <div style={{ textAlign: 'center', fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                {formStrings.buyToSellRatio}% Buy / {100 - parseInt(formStrings.buyToSellRatio || '50', 10)}% Sell
                            </div>
                        </div>

                        <div>
                            <label style={labelStyle}># of wallets</label>
                            <input
                                type="number"
                                value={formStrings.numberOfWallets}
                                onChange={(e) => setFormStrings({ ...formStrings, numberOfWallets: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                            />
                        </div>

                        {/* Separate Buy/Sell Trade Sizes Toggle */}
                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Separate Buy/Sell Trade Sizes</label>
                            {canUseSeparateTradeSizes ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <input
                                        type="checkbox"
                                        checked={useSeparateTradeSizes}
                                        onChange={(e) => setUseSeparateTradeSizes(e.target.checked)}
                                    />
                                    <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                                        Set different min/max trade sizes for buys vs sells
                                    </span>
                                </div>
                            ) : (
                                <div style={{ fontSize: "12px", color: "#6b7280", opacity: 0.7 }}>
                                    Requires 1M+ tokens or Pro tier
                                </div>
                            )}
                        </div>

                        {/* Expandable panel for separate trade sizes */}
                        {useSeparateTradeSizes && canUseSeparateTradeSizes && (
                            <div style={{
                                gridColumn: "span 2",
                                padding: "16px",
                                borderRadius: "12px",
                                background: "rgba(255, 255, 255, 0.03)",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                            }}>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                                    <div>
                                        <label style={labelStyle}>Buy Trade Size (Min/Max)</label>
                                        <div style={{ display: "flex", gap: "8px" }}>
                                            <input
                                                type="number"
                                                step="0.001"
                                                value={buyTradeSizeMin}
                                                onChange={(e) => setBuyTradeSizeMin(e.target.value)}
                                                className="input-dark"
                                                style={inputStyle}
                                            />
                                            <input
                                                type="number"
                                                step="0.001"
                                                value={buyTradeSizeMax}
                                                onChange={(e) => setBuyTradeSizeMax(e.target.value)}
                                                className="input-dark"
                                                style={inputStyle}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label style={labelStyle}>Sell Trade Size (Min/Max)</label>
                                        <div style={{ display: "flex", gap: "8px" }}>
                                            <input
                                                type="number"
                                                step="0.001"
                                                value={sellTradeSizeMin}
                                                onChange={(e) => setSellTradeSizeMin(e.target.value)}
                                                className="input-dark"
                                                style={inputStyle}
                                            />
                                            <input
                                                type="number"
                                                step="0.001"
                                                value={sellTradeSizeMax}
                                                onChange={(e) => setSellTradeSizeMax(e.target.value)}
                                                className="input-dark"
                                                style={inputStyle}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "10px" }}>
                                    <input
                                        type="checkbox"
                                        checked={autoRatioEnabled}
                                        onChange={(e) => setAutoRatioEnabled(e.target.checked)}
                                    />
                                    <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                                        Auto-calculate buy/sell ratio from trade sizes
                                    </span>
                                </div>
                                <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px", marginLeft: "22px" }}>
                                    Formula: buyRatio = avgSell / (avgBuy + avgSell) — keeps volume balanced
                                </div>
                            </div>
                        )}

                        <div>
                            <label style={labelStyle}>Max Volume ({quoteSymbol})</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={formStrings.maxVolume}
                                onChange={(e) => setFormStrings({ ...formStrings, maxVolume: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                                placeholder="Leave blank for unlimited"
                            />
                        </div>
                    </>
                )}

                {method === "extract" && (
                    <div style={{ gridColumn: "span 2" }}>
                        <label style={labelStyle} title={`The bot sells tokens whenever the pool's ${quoteSymbol} reserve rises above this level.`}>Pool {quoteSymbol} Floor</label>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <input
                                type="number"
                                step="0.1"
                                value={formStrings.reserveTargetInSOL}
                                onChange={(e) => setFormStrings({ ...formStrings, reserveTargetInSOL: e.target.value })}
                                className="input-dark"
                                style={{ ...inputStyle, flex: 1 }}
                            />
                            <button
                                type="button"
                                onClick={fetchPoolInfo}
                                disabled={poolInfoLoading}
                                style={{
                                    padding: "10px 14px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    background: "rgba(255,255,255,0.06)",
                                    color: "#d1d5db",
                                    fontSize: "12px",
                                    cursor: poolInfoLoading ? "wait" : "pointer",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {poolInfoLoading ? "..." : `Current: ${poolInfo?.quoteReserve?.toFixed(1) ?? '?'}`}
                            </button>
                        </div>
                    </div>
                )}

                {method === "inject" && (
                    <div style={{ gridColumn: "span 2" }}>
                        <label style={labelStyle}>Injection Amount ({quoteSymbol})</label>
                        <input
                            type="number"
                            step="0.1"
                            value={formStrings.injectionAmount}
                            onChange={(e) => setFormStrings({ ...formStrings, injectionAmount: e.target.value })}
                            className="input-dark"
                            style={inputStyle}
                        />
                    </div>
                )}

                {method === "defendFloor" && (
                    <>
                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Threshold Type</label>
                            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('price')}
                                    style={{
                                        flex: 1,
                                        padding: "12px",
                                        borderRadius: "12px",
                                        border: thresholdType === 'price' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'price' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'price' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'price' ? "600" : "400",
                                        fontSize: "14px",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                    }}
                                >
                                    Price
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('marketcap')}
                                    style={{
                                        flex: 1,
                                        padding: "12px",
                                        borderRadius: "12px",
                                        border: thresholdType === 'marketcap' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'marketcap' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'marketcap' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'marketcap' ? "600" : "400",
                                        fontSize: "14px",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                    }}
                                >
                                    Market Cap
                                </button>
                            </div>
                        </div>

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>
                                {thresholdType === 'price' ? 'Price Threshold (USD)' : 'Market Cap Threshold (USD)'}
                            </label>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <input
                                    type="number"
                                    step="0.000001"
                                    value={thresholdType === 'price' ? formStrings.priceThreshold : formStrings.mcThreshold}
                                    onChange={(e) => setFormStrings({
                                        ...formStrings,
                                        [thresholdType === 'price' ? 'priceThreshold' : 'mcThreshold']: e.target.value
                                    })}
                                    className="input-dark"
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder={thresholdType === 'price' ? 'e.g., 0.000050' : 'e.g., 50000'}
                                />
                                <button
                                    type="button"
                                    onClick={fetchPoolInfo}
                                    disabled={poolInfoLoading}
                                    style={{
                                        padding: "10px 14px", borderRadius: "12px",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        background: "rgba(255,255,255,0.06)",
                                        color: "#d1d5db", fontSize: "12px",
                                        cursor: poolInfoLoading ? "wait" : "pointer",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {poolInfoLoading ? "..." : `Price: $${poolInfo?.tokenPriceUSD?.toFixed(8) ?? '?'}`}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label style={labelStyle}>Recovery %</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={formStrings.recoveryThreshold}
                                onChange={(e) => setFormStrings({ ...formStrings, recoveryThreshold: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                                placeholder="e.g., 1"
                            />
                        </div>

                        <div style={{ gridColumn: "span 2", opacity: canUseTrailingStop ? 1 : 0.5 }}>
                            <label style={labelStyle}>
                                Trailing Stop {!canUseTrailingStop && <span style={{ color: "#6b7280" }}> Level 2</span>}
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: canUseTrailingStop ? "pointer" : "not-allowed", marginTop: "6px" }}>
                                <input
                                    type="checkbox"
                                    checked={enableTrailing}
                                    onChange={(e) => canUseTrailingStop && setEnableTrailing(e.target.checked)}
                                    disabled={!canUseTrailingStop}
                                />
                                Enable trailing stop
                            </label>
                            {enableTrailing && canUseTrailingStop && (
                                <div style={{ marginTop: "8px" }}>
                                    <label style={labelStyle}>Trail % Below Peak</label>
                                    <input
                                        type="number"
                                        step="1"
                                        min="1"
                                        max="50"
                                        value={formStrings.trailingPct}
                                        onChange={(e) => setFormStrings({ ...formStrings, trailingPct: e.target.value })}
                                        className="input-dark"
                                        style={inputStyle}
                                        placeholder="e.g., 10"
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}

                {(method === "stopLoss" || method === "takeProfit") && (
                    <>
                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Threshold Type</label>
                            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('price')}
                                    style={{
                                        flex: 1, padding: "12px", borderRadius: "12px",
                                        border: thresholdType === 'price' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'price' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'price' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'price' ? "600" : "400",
                                        fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                                    }}
                                >Price</button>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('marketcap')}
                                    style={{
                                        flex: 1, padding: "12px", borderRadius: "12px",
                                        border: thresholdType === 'marketcap' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'marketcap' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'marketcap' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'marketcap' ? "600" : "400",
                                        fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                                    }}
                                >Market Cap</button>
                            </div>
                        </div>

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>{thresholdType === 'price' ? 'Price Threshold (USD)' : 'Market Cap Threshold (USD)'}</label>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <input
                                    type="number"
                                    step="0.000001"
                                    value={thresholdType === 'price' ? formStrings.priceThreshold : formStrings.mcThreshold}
                                    onChange={(e) => setFormStrings({ ...formStrings, [thresholdType === 'price' ? 'priceThreshold' : 'mcThreshold']: e.target.value })}
                                    className="input-dark"
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder={thresholdType === 'price' ? 'e.g., 0.000050' : 'e.g., 50000'}
                                />
                                <button
                                    type="button"
                                    onClick={fetchPoolInfo}
                                    disabled={poolInfoLoading}
                                    style={{
                                        padding: "10px 14px", borderRadius: "12px",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        background: "rgba(255,255,255,0.06)",
                                        color: "#d1d5db", fontSize: "12px",
                                        cursor: poolInfoLoading ? "wait" : "pointer",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {poolInfoLoading ? "..." : `Price: $${poolInfo?.tokenPriceUSD?.toFixed(8) ?? '?'}`}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label style={labelStyle}>Recovery % (stop selling when)</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={formStrings.recoveryThreshold}
                                onChange={(e) => setFormStrings({ ...formStrings, recoveryThreshold: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                                placeholder={method === 'takeProfit' ? 'e.g., 2' : 'e.g., 1'}
                            />
                        </div>
                    </>
                )}

                {method === "accumulate" && (
                    <>
                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Trigger Mode</label>
                            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                                {(['schedule', 'price', 'marketcap'] as const).map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setTriggerType(t)}
                                        style={{
                                            flex: 1, padding: "10px", borderRadius: "12px",
                                            border: triggerType === t ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                            background: triggerType === t ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                            color: triggerType === t ? BRAND_PRIMARY : "white",
                                            fontWeight: triggerType === t ? "600" : "400",
                                            fontSize: "13px", cursor: "pointer", transition: "all 0.2s",
                                        }}
                                    >
                                        {t === 'schedule' ? 'Schedule' : t === 'price' ? 'Price' : 'Market Cap'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {triggerType !== 'schedule' && (
                            <div style={{ gridColumn: "span 2" }}>
                                <label style={labelStyle}>{triggerType === 'price' ? 'Buy below price (USD)' : 'Buy below market cap (USD)'}</label>
                                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                    <input
                                        type="number"
                                        step="0.000001"
                                        value={triggerType === 'price' ? formStrings.priceThreshold : formStrings.mcThreshold}
                                        onChange={(e) => setFormStrings({ ...formStrings, [triggerType === 'price' ? 'priceThreshold' : 'mcThreshold']: e.target.value })}
                                        className="input-dark"
                                        style={{ ...inputStyle, flex: 1 }}
                                        placeholder={triggerType === 'price' ? 'e.g., 0.000050' : 'e.g., 50000'}
                                    />
                                    <button
                                        type="button"
                                        onClick={fetchPoolInfo}
                                        disabled={poolInfoLoading}
                                        style={{
                                            padding: "10px 14px", borderRadius: "12px",
                                            border: "1px solid rgba(255,255,255,0.15)",
                                            background: "rgba(255,255,255,0.06)",
                                            color: "#d1d5db", fontSize: "12px",
                                            cursor: poolInfoLoading ? "wait" : "pointer",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {poolInfoLoading ? "..." : `Price: $${poolInfo?.tokenPriceUSD?.toFixed(8) ?? '?'}`}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Target Amount ({quoteSymbol})</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={formStrings.targetAmount}
                                onChange={(e) => setFormStrings({ ...formStrings, targetAmount: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                                placeholder="Leave blank for unlimited"
                            />
                        </div>
                    </>
                )}

                {method === "buyToPrice" && (
                    <>
                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Threshold Type</label>
                            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('price')}
                                    style={{
                                        flex: 1, padding: "12px", borderRadius: "12px",
                                        border: thresholdType === 'price' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'price' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'price' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'price' ? "600" : "400",
                                        fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                                    }}
                                >Price</button>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('marketcap')}
                                    style={{
                                        flex: 1, padding: "12px", borderRadius: "12px",
                                        border: thresholdType === 'marketcap' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'marketcap' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'marketcap' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'marketcap' ? "600" : "400",
                                        fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                                    }}
                                >Market Cap</button>
                            </div>
                        </div>

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>{thresholdType === 'price' ? 'Buy until price reaches (USD)' : 'Buy until market cap reaches (USD)'}</label>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <input
                                    type="number"
                                    step="0.000001"
                                    value={thresholdType === 'price' ? formStrings.priceThreshold : formStrings.mcThreshold}
                                    onChange={(e) => setFormStrings({ ...formStrings, [thresholdType === 'price' ? 'priceThreshold' : 'mcThreshold']: e.target.value })}
                                    className="input-dark"
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder={thresholdType === 'price' ? 'e.g., 0.000050' : 'e.g., 50000'}
                                />
                                <button
                                    type="button"
                                    onClick={fetchPoolInfo}
                                    disabled={poolInfoLoading}
                                    style={{
                                        padding: "10px 14px", borderRadius: "12px",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        background: "rgba(255,255,255,0.06)",
                                        color: "#d1d5db", fontSize: "12px",
                                        cursor: poolInfoLoading ? "wait" : "pointer",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {poolInfoLoading ? "..." : `Price: $${poolInfo?.tokenPriceUSD?.toFixed(8) ?? '?'}`}
                                </button>
                            </div>
                        </div>

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Max {quoteSymbol} to Spend</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={formStrings.targetAmount}
                                onChange={(e) => setFormStrings({ ...formStrings, targetAmount: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                                placeholder="Leave blank for unlimited"
                            />
                        </div>
                    </>
                )}

                {method === "sellToPrice" && (
                    <>
                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Threshold Type</label>
                            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('price')}
                                    style={{
                                        flex: 1, padding: "12px", borderRadius: "12px",
                                        border: thresholdType === 'price' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'price' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'price' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'price' ? "600" : "400",
                                        fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                                    }}
                                >Price</button>
                                <button
                                    type="button"
                                    onClick={() => setThresholdType('marketcap')}
                                    style={{
                                        flex: 1, padding: "12px", borderRadius: "12px",
                                        border: thresholdType === 'marketcap' ? `2px solid ${BRAND_PRIMARY}` : "1px solid rgba(255,255,255,0.2)",
                                        background: thresholdType === 'marketcap' ? "rgba(255, 123, 0, 0.2)" : "rgba(255,255,255,0.06)",
                                        color: thresholdType === 'marketcap' ? BRAND_PRIMARY : "white",
                                        fontWeight: thresholdType === 'marketcap' ? "600" : "400",
                                        fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                                    }}
                                >Market Cap</button>
                            </div>
                        </div>

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>{thresholdType === 'price' ? 'Sell until price drops to (USD)' : 'Sell until market cap drops to (USD)'}</label>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <input
                                    type="number"
                                    step="0.000001"
                                    value={thresholdType === 'price' ? formStrings.priceThreshold : formStrings.mcThreshold}
                                    onChange={(e) => setFormStrings({ ...formStrings, [thresholdType === 'price' ? 'priceThreshold' : 'mcThreshold']: e.target.value })}
                                    className="input-dark"
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder={thresholdType === 'price' ? 'e.g., 0.000050' : 'e.g., 50000'}
                                />
                                <button
                                    type="button"
                                    onClick={fetchPoolInfo}
                                    disabled={poolInfoLoading}
                                    style={{
                                        padding: "10px 14px", borderRadius: "12px",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        background: "rgba(255,255,255,0.06)",
                                        color: "#d1d5db", fontSize: "12px",
                                        cursor: poolInfoLoading ? "wait" : "pointer",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {poolInfoLoading ? "..." : `Price: $${poolInfo?.tokenPriceUSD?.toFixed(8) ?? '?'}`}
                                </button>
                            </div>
                        </div>

                        <div style={{ gridColumn: "span 2" }}>
                            <label style={labelStyle}>Max Tokens to Sell</label>
                            <input
                                type="number"
                                step="1"
                                min="0"
                                value={formStrings.maxSellTokens}
                                onChange={(e) => setFormStrings({ ...formStrings, maxSellTokens: e.target.value })}
                                className="input-dark"
                                style={inputStyle}
                                placeholder="Leave blank for unlimited"
                            />
                        </div>
                    </>
                )}
            </div>

            <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
                {isRunning && (
                    <button
                        onClick={handleUpdateParams}
                        style={{
                            flex: 1,
                            padding: "12px",
                            borderRadius: "12px",
                            background: "rgba(255, 123, 0, 0.2)",
                            color: BRAND_PRIMARY,
                            border: "1px solid rgba(255, 123, 0, 0.3)",
                            fontWeight: "500",
                            fontSize: "14px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}
                    >
                        Update Params
                    </button>
                )}
                <button
                    onClick={handleToggle}
                    style={{
                        flex: 1,
                        padding: "12px",
                        borderRadius: "12px",
                        fontWeight: "600",
                        border: "none",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        ...(isRunning
                            ? { background: "rgba(239, 68, 68, 0.2)", color: "#f87171" }
                            : { background: "rgba(0, 218, 243, 0.2)", color: BRAND_ACCENT }),
                    }}
                >
                    {isRunning ? "Stop" : "Start"} {
                        method === "defendFloor" ? "Defend Floor" :
                        method === "stopLoss" ? "Stop Loss" :
                        method === "takeProfit" ? "Take Profit" :
                        method === "buyToPrice" ? "Buy To Price" :
                        method === "sellToPrice" ? "Sell To Price" :
                        method.charAt(0).toUpperCase() + method.slice(1)
                    }
                </button>
            </div>
        </div>
    );
};

export default BotMethodForm;
